import React, { useEffect } from "react";
import { Box, Container, Link, Paper, Typography } from "@mui/material";
import { Link as RouterLink } from "react-router-dom";

interface LegalPageLayoutProps {
  title: string;
  description: string;
  children: React.ReactNode;
}

export const LegalSection: React.FC<{
  title: string;
  children: React.ReactNode;
}> = ({ title, children }) => (
  <Box component="section" sx={{ mb: 4 }}>
    <Typography
      variant="h5"
      component="h2"
      sx={{ color: "text.primary", fontWeight: 850, mb: 1.5 }}
    >
      {title}
    </Typography>
    <Box
      sx={{
        color: "rgba(236,239,244,0.78)",
        lineHeight: 1.75,
        "& p": { mt: 0, mb: 1.5 },
        "& li": { mb: 0.8 },
        "& a": { color: "secondary.main" },
      }}
    >
      {children}
    </Box>
  </Box>
);

const LegalPageLayout: React.FC<LegalPageLayoutProps> = ({
  title,
  description,
  children,
}) => {
  useEffect(() => {
    document.title = `${title} | Ray Jones`;
    window.scrollTo(0, 0);
  }, [title]);

  return (
    <Box sx={{ minHeight: "100vh", bgcolor: "background.default", py: { xs: 5, md: 8 } }}>
      <Container maxWidth="md">
        <Link
          component={RouterLink}
          to="/"
          underline="hover"
          sx={{ color: "secondary.main", fontWeight: 800 }}
        >
          ← Back to raymondjones.dev
        </Link>
        <Paper
          elevation={0}
          sx={{
            mt: 3,
            p: { xs: 2.5, sm: 4, md: 6 },
            bgcolor: "rgba(59,66,82,0.76)",
            border: "1px solid #434C5E",
            borderRadius: 4,
          }}
        >
          <Typography
            variant="h2"
            component="h1"
            sx={{
              color: "text.primary",
              fontWeight: 900,
              letterSpacing: "-0.035em",
              fontSize: { xs: "2.25rem", md: "3.25rem" },
              mb: 1,
            }}
          >
            {title}
          </Typography>
          <Typography sx={{ color: "text.secondary", lineHeight: 1.7, mb: 1 }}>
            {description}
          </Typography>
          <Typography variant="caption" sx={{ color: "rgba(236,239,244,0.58)" }}>
            Effective and last updated: July 28, 2026
          </Typography>
          <Box sx={{ mt: 5 }}>{children}</Box>
        </Paper>
      </Container>
    </Box>
  );
};

export default LegalPageLayout;
